import React from 'react';
import { useTranslation } from 'react-i18next';

import PolicyNavigation from './PolicyNavigation';

interface RefundCancellationContentProps {
  onNavigate?: (view: string) => void;
}

const RefundCancellationContent: React.FC<RefundCancellationContentProps> = ({ onNavigate }) => {
  const { t } = useTranslation();
  return (
    <div className="max-w-4xl mx-auto text-gray-900 pt-8">
      <h1 className="text-3xl font-bold mb-6 text-gray-900">{t('legal.refundTitle')}</h1>
      
      <div className="prose max-w-none">
        <p className="text-sm text-gray-600 mb-6">
          <strong>{t('legal.refund.effectiveDate')}:</strong> June 2025<br />
          <strong>{t('legal.refund.lastUpdated')}:</strong> October 2025
        </p>

        <p className="text-lg text-gray-700 mb-6">
          {t('legal.refund.intro')}
        </p>

        <div className="mb-8 p-4  ">
          <p className="text-gray-700">
            {t('legal.refund.quickSummary')}
          </p>
        </div>

        {/* Section 1 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.refund.s1Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s1_1Title')}</h3>
        <p className="text-gray-700 mb-4">
          {t('legal.refund.s1_1Intro')}
        </p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.planFree')}</li>
          <li>{t('legal.refund.planBasic')}</li>
          <li>{t('legal.refund.planPremium')}</li>
          <li>{t('legal.refund.planPro')}</li>
          <li>{t('legal.refund.planEnterprise')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s1_2Title')}</h3>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.billMonthly')}</li>
          <li>{t('legal.refund.billQuarterly')}</li>
          <li>{t('legal.refund.billAnnual')}</li>
          <li>{t('legal.refund.billCustom')}</li>
        </ul>

        {/* Section 2 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.refund.s2Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s2_1Title')}</h3>
        <p className="text-gray-700 mb-4">
          {t('legal.refund.cancelIntro')}
        </p>
        
        <div className=" p-6-lg mb-4">
          <h4 className="text-lg font-semibold mb-3 text-blue-400">{t('legal.refund.method1Title')}</h4>
          <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
            <li>{t('legal.refund.method1Step1')}</li>
            <li>{t('legal.refund.method1Step2')}</li>
            <li>{t('legal.refund.method1Step3')}</li>
            <li>{t('legal.refund.method1Step4')}</li>
            <li>{t('legal.refund.method1Step5')}</li>
            <li>{t('legal.refund.method1Step6')}</li>
          </ol>
        </div>

        <div className=" p-6-lg mb-4">
          <h4 className="text-lg font-semibold mb-3 text-blue-400">{t('legal.refund.method2Title')}</h4>
          <p className="text-gray-700 mb-2">
            {t('legal.refund.emailUs')} <a href="mailto:support@gograbjob.com" className="text-blue-400 hover:text-blue-300">support@gograbjob.com</a>
          </p>
          <p className="text-sm text-gray-600">
            {t('legal.refund.includeEmail')}
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s2_2Title')}</h3>
        <div className=" p-6-lg mb-4">
          <h4 className="text-lg font-semibold mb-3">✅ {t('legal.refund.accessTitle')}</h4>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>{t('legal.refund.access1')}</li>
            <li>{t('legal.refund.access2')}</li>
            <li>{t('legal.refund.access3')}</li>
            <li>{t('legal.refund.access4')}</li>
            <li>{t('legal.refund.access5')}</li>
            <li>{t('legal.refund.access6')}</li>
          </ul>
        </div>

        <div className="bg-yellow-900/20 p-6 border-l-4 border-yellow-500 mb-4">
          <h4 className="text-lg font-semibold mb-3">⚠️ {t('legal.refund.afterTitle')}</h4>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>{t('legal.refund.after1')}</li>
            <li>{t('legal.refund.after2')}</li>
            <li>{t('legal.refund.after3')}</li>
            <li>{t('legal.refund.after4')}</li>
            <li>{t('legal.refund.after5')}</li>
            <li>{t('legal.refund.after6')}</li>
          </ul>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s2_3Title')}</h3>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.terms1')}</li>
          <li>{t('legal.refund.terms2')}</li>
          <li>{t('legal.refund.terms3')}</li>
          <li>{t('legal.refund.terms4')}</li>
          <li>{t('legal.refund.terms5')}</li>
          <li>{t('legal.refund.terms6')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s2_4Title')}</h3>
        <p className="text-gray-700 mb-4">
          {t('legal.refund.trialIntro')}
        </p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.trial1')}</li>
          <li>{t('legal.refund.trial2')}</li>
          <li>{t('legal.refund.trial3')}</li>
          <li>{t('legal.refund.trial4')}</li>
          <li>{t('legal.refund.trial5')}</li>
        </ul>

        {/* Section 3 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.refund.s3Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s3_1Title')}</h3>
        <div className=" p-6  mb-4">
          <p className="text-gray-700 mb-4">
            {t('legal.refund.guaranteePara')}
          </p>
          
          <h4 className="text-lg font-semibold mb-3">{t('legal.refund.eligibilityTitle')}</h4>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>✅ {t('legal.refund.elig1')}</li>
            <li>✅ {t('legal.refund.elig2')}</li>
            <li>✅ {t('legal.refund.elig3')}</li>
            <li>✅ {t('legal.refund.elig4')}</li>
            <li>✅ {t('legal.refund.elig5')}</li>
          </ul>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s3_2Title')}</h3>
        <p className="text-gray-700 mb-4">
          {t('legal.refund.requestIntro')}
        </p>
        
        <div className=" p-6-lg mb-4">
          <h4 className="text-lg font-semibold mb-3 text-blue-400">{t('legal.refund.option1Title')}</h4>
          <ol className="list-decimal list-inside text-gray-700 space-y-2 ml-4">
            <li>{t('legal.refund.option1Step1')}</li>
            <li>{t('legal.refund.option1Step2')}</li>
            <li>{t('legal.refund.option1Step3')}</li>
            <li>{t('legal.refund.option1Step4')}</li>
            <li>{t('legal.refund.option1Step5')}</li>
          </ol>
        </div>

        <div className=" p-6-lg mb-4">
          <h4 className="text-lg font-semibold mb-3 text-blue-400">{t('legal.refund.option2Title')}</h4>
          <p className="text-gray-700 mb-3">
            <a href="mailto:support@gograbjob.com" className="text-blue-400 hover:text-blue-300">support@gograbjob.com</a>
          </p>
          <p className="text-gray-700 mb-3">
            <strong>{t('legal.refund.includeInEmail')}</strong>
          </p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>{t('legal.refund.include1')}</li>
            <li>{t('legal.refund.include2')}</li>
            <li>{t('legal.refund.include3')}</li>
            <li>{t('legal.refund.include4')}</li>
          </ul>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s3_3Title')}</h3>
        <div className=" p-6-lg mb-4">
          <table className="w-full text-sm text-gray-700">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2">{t('legal.refund.tableStep')}</th>
                <th className="text-left py-2">{t('legal.refund.tableTimeline')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-700/50"><td className="py-2">{t('legal.refund.requestReview')}</td><td className="py-2">{t('legal.refund.days1_2')}</td></tr>
              <tr className="border-b border-gray-700/50"><td className="py-2">{t('legal.refund.approval')}</td><td className="py-2">{t('legal.refund.immediate')}</td></tr>
              <tr className="border-b border-gray-700/50"><td className="py-2">{t('legal.refund.creditCard')}</td><td className="py-2">{t('legal.refund.days5_10')}</td></tr>
              <tr className="border-b border-gray-700/50"><td className="py-2">{t('legal.refund.upi')}</td><td className="py-2">{t('legal.refund.days3_7')}</td></tr>
              <tr className="border-b border-gray-700/50"><td className="py-2">{t('legal.refund.netBanking')}</td><td className="py-2">{t('legal.refund.days3_7')}</td></tr>
              <tr><td className="py-2">{t('legal.refund.wallets')}</td><td className="py-2">{t('legal.refund.days1_3')}</td></tr>
            </tbody>
          </table>
          <p className="text-sm text-gray-600 mt-3">
            {t('legal.refund.noteProcessing')}
          </p>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s3_4Title')}</h3>
        
        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-green-400">✅ {t('legal.refund.eligibleTitle')}</h4>
          <div className=" p-4-lg">
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>{t('legal.refund.eligible1')}</li>
              <li>{t('legal.refund.eligible2')}</li>
              <li>{t('legal.refund.eligible3')}</li>
              <li>{t('legal.refund.eligible4')}</li>
              <li>{t('legal.refund.eligible5')}</li>
              <li>{t('legal.refund.eligible6')}</li>
              <li>{t('legal.refund.eligible7')}</li>
            </ul>
          </div>
        </div>

        <div className="mb-6">
          <h4 className="text-lg font-semibold mb-3 text-red-400">❌ {t('legal.refund.notEligibleTitle')}</h4>
          <div className="bg-red-900/20 p-4-lg">
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
              <li>{t('legal.refund.notEligible1')}</li>
              <li>{t('legal.refund.notEligible2')}</li>
              <li>{t('legal.refund.notEligible3')}</li>
              <li>{t('legal.refund.notEligible4')}</li>
              <li>{t('legal.refund.notEligible5')}</li>
              <li>{t('legal.refund.notEligible6')}</li>
              <li>{t('legal.refund.notEligible7')}</li>
              <li>{t('legal.refund.notEligible8')}</li>
              <li>{t('legal.refund.notEligible9')}</li>
              <li>{t('legal.refund.notEligible10')}</li>
            </ul>
          </div>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s3_5Title')}</h3>
        <div className=" p-4  mb-4">
          <p className="text-gray-700">
            {t('legal.refund.partialPara')}
          </p>
        </div>

        {/* Section 4 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.refund.s4Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s4_1Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.medicalIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.medical1')}</li>
          <li>{t('legal.refund.medical2')}</li>
          <li>{t('legal.refund.medical3')}</li>
          <li>{t('legal.refund.medical4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s4_2Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.techIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.tech1')}</li>
          <li>{t('legal.refund.tech2')}</li>
          <li>{t('legal.refund.tech3')}</li>
          <li>{t('legal.refund.tech4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s4_3Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.outagesIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.outages1')}</li>
          <li>{t('legal.refund.outages2')}</li>
          <li>{t('legal.refund.outages3')}</li>
          <li>{t('legal.refund.outages4')}</li>
        </ul>

        {/* Section 5 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.refund.s5Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s5_1Title')}</h3>
        <div className=" p-4-lg mb-4">
          <p className="text-gray-700 mb-3">{t('legal.refund.aiCreditsIntro')}</p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>{t('legal.refund.aiCredits1')}</li>
            <li>{t('legal.refund.aiCredits2')}</li>
            <li>{t('legal.refund.aiCredits3')}</li>
            <li>{t('legal.refund.aiCredits4')}</li>
            <li>{t('legal.refund.aiCredits5')}</li>
          </ul>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s5_2Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.batchIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.batch1')}</li>
          <li>{t('legal.refund.batch2')}</li>
          <li>{t('legal.refund.batch3')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s5_3Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.unusedIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.unused1')}</li>
          <li>{t('legal.refund.unused2')}</li>
          <li>{t('legal.refund.unused3')}</li>
          <li>{t('legal.refund.unused4')}</li>
        </ul>

        {/* Section 6 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.refund.s6Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s6_1Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.entCancelIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.entCancel1')}</li>
          <li>{t('legal.refund.entCancel2')}</li>
          <li>{t('legal.refund.entCancel3')}</li>
          <li>{t('legal.refund.entCancel4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s6_2Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.entRefundIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.entRefund1')}</li>
          <li>{t('legal.refund.entRefund2')}</li>
          <li>{t('legal.refund.entRefund3')}</li>
          <li>{t('legal.refund.entRefund4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s6_3Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.bulkIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.bulk1')}</li>
          <li>{t('legal.refund.bulk2')}</li>
          <li>{t('legal.refund.bulk3')}</li>
        </ul>

        {/* Section 7 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.refund.s7Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s7_1Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.refundDestIntro')}</p>
        <div className=" p-4-lg mb-4">
          <table className="w-full text-sm text-gray-700">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="text-left py-2">{t('legal.refund.payMethod')}</th>
                <th className="text-left py-2">{t('legal.refund.refundDest')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-700/50">
                <td className="py-2">{t('legal.refund.creditCard')}</td>
                <td className="py-2">{t('legal.refund.sameCard')}</td>
              </tr>
              <tr className="border-b border-gray-700/50">
                <td className="py-2">{t('legal.refund.upi')}</td>
                <td className="py-2">{t('legal.refund.originalUpi')}</td>
              </tr>
              <tr className="border-b border-gray-700/50">
                <td className="py-2">{t('legal.refund.netBanking')}</td>
                <td className="py-2">{t('legal.refund.originalBank')}</td>
              </tr>
              <tr className="border-b border-gray-700/50">
                <td className="py-2">{t('legal.refund.wallets')}</td>
                <td className="py-2">{t('legal.refund.sameWallet')}</td>
              </tr>
              <tr>
                <td className="py-2">{t('legal.refund.paymentGateway')}</td>
                <td className="py-2">{t('legal.refund.originalSource')}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s7_2Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.expiredIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.expired1')}</li>
          <li>{t('legal.refund.expired2')}</li>
          <li>{t('legal.refund.expired3')}</li>
          <li>{t('legal.refund.expired4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s7_3Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.currencyIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.currency1')}</li>
          <li>{t('legal.refund.currency2')}</li>
          <li>{t('legal.refund.currency3')}</li>
          <li>{t('legal.refund.currency4')}</li>
          <li>{t('legal.refund.currency5')}</li>
        </ul>

        {/* Section 8 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.refund.s8Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s8_1Title')}</h3>
        <div className=" p-6  mb-4">
          <p className="text-gray-700 mb-3">
            <strong>⚠️ {t('legal.refund.chargebackWarning')}</strong>
          </p>
          <p className="text-gray-700 mb-3">{t('legal.refund.chargebackIntro')}</p>
          <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
            <li>{t('legal.refund.chargeback1')}</li>
            <li>{t('legal.refund.chargeback2')}</li>
            <li>{t('legal.refund.chargeback3')}</li>
            <li>{t('legal.refund.chargeback4')}</li>
          </ul>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s8_2Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.consequencesIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.consequences1')}</li>
          <li>{t('legal.refund.consequences2')}</li>
          <li>{t('legal.refund.consequences3')}</li>
          <li>{t('legal.refund.consequences4')}</li>
          <li>{t('legal.refund.consequences5')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s8_3Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.validIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.valid1')}</li>
          <li>{t('legal.refund.valid2')}</li>
          <li>{t('legal.refund.valid3')}</li>
        </ul>
        <p className="text-gray-700 mb-4">{t('legal.refund.validContact')}</p>

        {/* Section 9 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.refund.s9Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s9_1Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.gstIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.gst1')}</li>
          <li>{t('legal.refund.gst2')}</li>
          <li>{t('legal.refund.gst3')}</li>
          <li>{t('legal.refund.gst4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s9_2Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.intlTaxIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.intlTax1')}</li>
          <li>{t('legal.refund.intlTax2')}</li>
          <li>{t('legal.refund.intlTax3')}</li>
          <li>{t('legal.refund.intlTax4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s9_3Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.feesIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.fees1')}</li>
          <li>{t('legal.refund.fees2')}</li>
          <li>{t('legal.refund.fees3')}</li>
        </ul>

        {/* Section 10 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.refund.s10Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s10_1Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.discountIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.discount1')}</li>
          <li>{t('legal.refund.discount2')}</li>
          <li>{t('legal.refund.discount3')}</li>
          <li>{t('legal.refund.discount4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s10_2Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.bundleIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.bundle1')}</li>
          <li>{t('legal.refund.bundle2')}</li>
          <li>{t('legal.refund.bundle3')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s10_3Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.referralIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.referral1')}</li>
          <li>{t('legal.refund.referral2')}</li>
          <li>{t('legal.refund.referral3')}</li>
          <li>{t('legal.refund.referral4')}</li>
        </ul>

        {/* Section 11 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.refund.s11Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s11_1Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.reactivateIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.reactivate1')}</li>
          <li>{t('legal.refund.reactivate2')}</li>
          <li>{t('legal.refund.reactivate3')}</li>
          <li>{t('legal.refund.reactivate4')}</li>
          <li>{t('legal.refund.reactivate5')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s11_2Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.upgradeIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.upgrade1')}</li>
          <li>{t('legal.refund.upgrade2')}</li>
          <li>{t('legal.refund.upgrade3')}</li>
          <li>{t('legal.refund.upgrade4')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s11_3Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.downgradeIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.downgrade1')}</li>
          <li>{t('legal.refund.downgrade2')}</li>
          <li>{t('legal.refund.downgrade3')}</li>
          <li>{t('legal.refund.downgrade4')}</li>
        </ul>

        {/* Section 12 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.refund.s12Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s12_1Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.changesIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.changes1')}</li>
          <li>{t('legal.refund.changes2')}</li>
          <li>{t('legal.refund.changes3')}</li>
          <li>{t('legal.refund.changes4')}</li>
          <li>{t('legal.refund.changes5')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s12_2Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.priceIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.price1')}</li>
          <li>{t('legal.refund.price2')}</li>
          <li>{t('legal.refund.price3')}</li>
          <li>{t('legal.refund.price4')}</li>
        </ul>

        {/* Section 13 */}
        <h2 className="text-2xl font-semibold mt-8 mb-4">{t('legal.refund.s13Title')}</h2>
        
        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s13_1Title')}</h3>
        <div className=" p-6-lg mb-4">
          <p className="text-gray-700 mb-4"><strong>{t('legal.refund.contactIntro')}</strong></p>
          <div className="space-y-3">
            <div>
              <p className="text-gray-700"><strong>📧 {t('legal.refund.emailSupport')}</strong></p>
              <p className="text-lg">
                <a href="mailto:support@gograbjob.com" className="text-blue-400 hover:text-blue-300">support@gograbjob.com</a>
              </p>
              <p className="text-sm text-gray-600">{t('legal.refund.responseTime')}</p>
            </div>
            <div>
              <p className="text-gray-700"><strong>🌐 {t('legal.refund.website')}</strong></p>
              <p className="text-lg">
                <a href="https://www.gograbjob.com" className="text-blue-400 hover:text-blue-300">www.gograbjob.com</a>
              </p>
            </div>
            <div>
              <p className="text-gray-700"><strong>💬 {t('legal.refund.liveChat')}</strong></p>
              <p className="text-sm text-gray-600">{t('legal.refund.liveChatHours')}</p>
            </div>
          </div>
        </div>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s13_2Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.infoIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.info1')}</li>
          <li>{t('legal.refund.info2')}</li>
          <li>{t('legal.refund.info3')}</li>
          <li>{t('legal.refund.info4')}</li>
          <li>{t('legal.refund.info5')}</li>
          <li>{t('legal.refund.info6')}</li>
        </ul>

        <h3 className="text-xl font-semibold mt-6 mb-3">{t('legal.refund.s13_3Title')}</h3>
        <p className="text-gray-700 mb-4">{t('legal.refund.hoursIntro')}</p>
        <ul className="list-disc list-inside text-gray-700 mb-4 space-y-2 ml-4">
          <li>{t('legal.refund.hours1')}</li>
          <li>{t('legal.refund.hours2')}</li>
          <li>{t('legal.refund.hours3')}</li>
        </ul>

        {/* Quick Reference Guide */}
        <div className="mt-8 p-6  ">
          <h3 className="text-xl font-semibold mb-4">{t('legal.refund.quickRefTitle')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-700">
            <div>
              <p className="font-semibold mb-2">✅ {t('legal.refund.yesTitle')}</p>
              <ul className="text-sm space-y-1">
                <li>• {t('legal.refund.yes1')}</li>
                <li>• {t('legal.refund.yes2')}</li>
                <li>• {t('legal.refund.yes3')}</li>
                <li>• {t('legal.refund.yes4')}</li>
                <li>• {t('legal.refund.yes5')}</li>
                <li>• {t('legal.refund.yes6')}</li>
              </ul>
            </div>
            <div>
              <p className="font-semibold mb-2">❌ {t('legal.refund.noTitle')}</p>
              <ul className="text-sm space-y-1">
                <li>• {t('legal.refund.no1')}</li>
                <li>• {t('legal.refund.no2')}</li>
                <li>• {t('legal.refund.no3')}</li>
                <li>• {t('legal.refund.no4')}</li>
                <li>• {t('legal.refund.no5')}</li>
                <li>• {t('legal.refund.no6')}</li>
              </ul>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-gray-700">
            <p className="font-semibold mb-2">⏰ {t('legal.refund.timelinesTitle')}</p>
            <ul className="text-sm space-y-1">
              <li>• {t('legal.refund.timeline1')}</li>
              <li>• {t('legal.refund.timeline2')}</li>
              <li>• {t('legal.refund.timeline3')}</li>
              <li>• {t('legal.refund.timeline4')}</li>
            </ul>
          </div>
        </div>

        {/* Final Summary */}
        <div className="mt-8 p-6  ">
          <p className="text-gray-700">{t('legal.refund.promise')}</p>
        </div>

        {/* Related Links */}
        <div className="mt-8 p-4 -lg">
          <p className="text-sm text-gray-600 mb-2"><strong>{t('legal.refund.relatedTitle')}</strong></p>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• <a href="/terms-of-service" className="text-blue-400 hover:text-blue-300">{t('legal.refund.termsLink')}</a></li>
            <li>• <a href="/privacy-policy" className="text-blue-400 hover:text-blue-300">{t('legal.refund.privacyLink')}</a></li>
            <li>• <a href="/pricing" className="text-blue-400 hover:text-blue-300">{t('legal.refund.pricingLink')}</a></li>
          </ul>
        </div>
      </div>

      {/* Related Policies Navigation */}
      <PolicyNavigation currentPolicy="refund" onNavigate={onNavigate} />
    </div>
  );
};

export default RefundCancellationContent;
